import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { superApi } from './SuperAdminAuth';
import {
  ArrowLeft, FileText, ExternalLink, RefreshCw, Download,
  CreditCard, AlertTriangle,
} from 'lucide-react';

const fmt = (amt, cur = 'usd') => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: (cur || 'usd').toUpperCase(), maximumFractionDigits: 2,
}).format(amt || 0);

const SuperBillingDetailPage = () => {
  const { companyId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refundingId, setRefundingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await superApi.get(`/super/billing/${companyId}/invoices`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [companyId]);

  const refund = async (chargeId) => {
    if (!window.confirm(`Refund charge ${chargeId}? This cannot be undone.`)) return;
    setRefundingId(chargeId);
    try {
      const r = await superApi.post(`/super/billing/${companyId}/refund`, { charge_id: chargeId });
      toast.success(`Refunded ${fmt(r.data.amount)} · ${r.data.status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    } finally {
      setRefundingId(null);
    }
  };

  if (loading) return <div className="p-8 text-slate-400">Loading billing detail…</div>;
  if (!data) return null;

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="super-billing-detail">
      <Link to="/superadmin/billing" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-amber-400 mb-4">
        <ArrowLeft className="w-3 h-3" /> Back to billing
      </Link>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-white">{data.company?.name}</h1>
        <button onClick={load} className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs text-slate-200 inline-flex items-center gap-2">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {data.stripe_error && (
        <div className="mb-6 bg-rose-500/10 border border-rose-500/30 rounded-xl p-3 flex items-start gap-2 text-sm text-rose-300">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Stripe error</div>
            <div className="text-xs font-mono mt-0.5">{data.stripe_error}</div>
          </div>
        </div>
      )}

      {/* Stripe invoices */}
      <section className="bg-slate-900 border border-slate-800 rounded-xl mb-6 overflow-hidden">
        <header className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-amber-400" />
          <h2 className="text-sm font-semibold text-white">Stripe invoices ({data.stripe_invoices?.length || 0})</h2>
        </header>
        {(!data.stripe_invoices || data.stripe_invoices.length === 0) ? (
          <div className="p-5 text-sm text-slate-500">No Stripe invoices. {data.stripe_error ? 'See error above.' : 'Tenant may not have a Stripe customer yet.'}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-950 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Invoice</th>
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Amount</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.stripe_invoices.map((inv) => (
                <tr key={inv.id} className="border-t border-slate-800">
                  <td className="px-4 py-2 text-slate-300">{inv.number || inv.id}</td>
                  <td className="px-4 py-2 text-slate-500">{inv.created ? new Date(inv.created * 1000).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      inv.status === 'paid' ? 'bg-emerald-500/10 text-emerald-300' :
                      inv.status === 'open' ? 'bg-amber-500/10 text-amber-300' :
                      'bg-slate-700 text-slate-300'
                    }`}>{inv.status}</span>
                  </td>
                  <td className="px-4 py-2 text-right text-slate-200 font-mono">{fmt(inv.amount_paid || inv.amount_due, inv.currency)}</td>
                  <td className="px-4 py-2 text-right space-x-2">
                    {inv.hosted_invoice_url && (
                      <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-amber-400 hover:underline">
                        <ExternalLink className="w-3 h-3" /> View
                      </a>
                    )}
                    {inv.invoice_pdf && (
                      <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-amber-400">
                        <Download className="w-3 h-3" /> PDF
                      </a>
                    )}
                    {inv.status === 'paid' && inv.id && (
                      <button
                        onClick={() => refund(inv.id.replace('in_', 'ch_'))}
                        disabled={refundingId === inv.id}
                        className="text-xs text-rose-400 hover:text-rose-300 disabled:opacity-50"
                        data-testid={`refund-btn-${inv.id}`}
                      >
                        Refund
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Shopify charges */}
      <section className="bg-slate-900 border border-slate-800 rounded-xl mb-6 overflow-hidden">
        <header className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
          <FileText className="w-4 h-4 text-emerald-400" />
          <h2 className="text-sm font-semibold text-white">Shopify charges ({data.shopify_charges?.length || 0})</h2>
        </header>
        {(!data.shopify_charges || data.shopify_charges.length === 0) ? (
          <div className="p-5 text-sm text-slate-500">No Shopify billing history.</div>
        ) : (
          <div className="divide-y divide-slate-800">
            {data.shopify_charges.map((c, i) => (
              <div key={i} className="px-5 py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="text-slate-300">{c.plan_name || c.charge_type || 'Charge'}</div>
                  <div className="text-xs text-slate-500">{new Date(c.created_at || c.activated_on || Date.now()).toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className="text-slate-200 font-mono">{fmt(c.price, 'USD')}</div>
                  <div className="text-xs text-slate-500">{c.status}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Local transactions (webhook log) */}
      {(data.local_transactions || []).length > 0 && (
        <section className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <header className="px-5 py-3 border-b border-slate-800">
            <h2 className="text-sm font-semibold text-white">Local transactions ({data.local_transactions.length})</h2>
          </header>
          <div className="divide-y divide-slate-800">
            {data.local_transactions.map((t, i) => (
              <div key={i} className="px-5 py-2.5 text-sm flex items-center justify-between">
                <div className="text-slate-400 text-xs font-mono">{t.stripe_charge_id || t.id}</div>
                <div className="text-slate-200 font-mono">{fmt(t.amount / 100, t.currency)}</div>
                <div className="text-xs text-slate-500">{t.status}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default SuperBillingDetailPage;
